import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Industry, User } from '@core/models/user.model';
import { createOptionalSignal } from '@core/utils/signal.utilities';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { AdpJwtPayload } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/types/jwt-payload.types';
import { ResponseUserGrantDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/dtos/response.user-grant.dto';
import {
  NamedRole,
  NamedRoleMap,
} from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { ResponseLeanOrganizationDto } from '@kerberos-compliance/compliance-shared-ts/features/organization/dtos/response.lean-organization.dto';
import { hydrateMultiple, StorageService } from '@kerberos-compliance/compliance-shared-ui';
import { jwtDecode } from 'jwt-decode';
import {
  AuthSource,
  AuthStateTokenAndOwnGrant,
  AuthStateTokens,
  FeatureRoleMap,
  FeaturesAmlDeskWithGrants,
  GrantLoadingState,
  GrantLoadingStateMap,
  LogoutReason,
} from '@/auth/types/auth.types';
import type { StorageServiceKeys } from '@/storage/types/storage.types';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly storageService = inject(StorageService);

  private readonly userSignal = createOptionalSignal<User>();

  private readonly logoutReasonSignal = createOptionalSignal<LogoutReason>();

  private readonly ownGrantsSignal = createOptionalSignal<readonly ResponseUserGrantDto[]>();

  private readonly organizationsSignal = createOptionalSignal<readonly ResponseLeanOrganizationDto[]>();

  private readonly organizationIdSignal = createOptionalSignal<string>();

  private readonly organizationIndustrySignal = createOptionalSignal<Industry>();

  private readonly errorMessageSignal = createOptionalSignal<string>();

  private readonly grantLoadingCompleteSignal = signal<boolean>(false);

  private readonly grantLoadingStateSignal = signal<GrantLoadingState>(GrantLoadingStateMap.Idle);

  private readonly tokensSignal = signal<AuthStateTokens>({
    // eslint-disable-next-line unicorn/no-null
    accessTokenKerberos: null,
    // eslint-disable-next-line unicorn/no-null
    refreshTokenKerberos: null,
    // eslint-disable-next-line unicorn/no-null
    accessTokenHanko: null,
  });

  private readonly authSourceMarkerSignal = createOptionalSignal<AuthSource>();

  public readonly activeOrganizationGrant = computed<ResponseUserGrantDto | undefined>(() => {
    const ownGrants = this.ownGrantsSignal();
    if (!ownGrants || ownGrants.length === 0) {
      return;
    }
    const selectedOrganizationId = this.organizationIdSignal();
    if (!selectedOrganizationId) {
      return ownGrants[0];
    }
    const organizationGrants = ownGrants.filter((grant) => grant.organizationId === selectedOrganizationId);
    if (organizationGrants.length === 0) {
      return;
    }

    const prioritizedRoles: NamedRole[] = [
      NamedRoleMap.RiskAnalysisCustomer,
      NamedRoleMap.WorkItemUser,
      NamedRoleMap.KycRiskEvaluationApiUser,
      NamedRoleMap.LocationAnalysisUser,
      NamedRoleMap.AcademyCoordinator,
      NamedRoleMap.AcademyUser,
    ];
    for (const role of prioritizedRoles) {
      const roleGrant = organizationGrants.find((grant) => grant.roleDisplayName === role);
      if (roleGrant) {
        return roleGrant;
      }
    }
    return organizationGrants[0];
  });

  public readonly user = this.userSignal.asReadonly();

  public readonly currentUserId = computed<UUID | undefined>(() =>
    this.getKerberosUserId(this.tokensSignal().accessTokenKerberos),
  );

  public readonly logoutReason = this.logoutReasonSignal.asReadonly();

  public readonly ownGrants = this.ownGrantsSignal.asReadonly();

  public readonly organizations = this.organizationsSignal.asReadonly();

  public readonly organizationId = this.organizationIdSignal.asReadonly();

  public readonly organizationIndustry = this.organizationIndustrySignal.asReadonly();

  public readonly grantLoadingComplete = this.grantLoadingCompleteSignal.asReadonly();

  public readonly grantLoadingState = this.grantLoadingStateSignal.asReadonly();

  public readonly authTokens = computed(() => this.tokensSignal());

  public readonly authSourceMarker = this.authSourceMarkerSignal.asReadonly();

  public readonly isUserLoggedIn = computed(
    () => Boolean(this.userSignal()) && Boolean(this.tokensSignal().accessTokenKerberos),
  );

  public readonly roleDisplayNames = computed(
    () => this.ownGrantsSignal()?.map((grant) => grant.roleDisplayName) ?? [],
  );

  public readonly activeGrantUserAndOwnGrants = computed(() => ({
    activeGrant: this.activeOrganizationGrant(),
    user: this.userSignal(),
    grants: this.ownGrantsSignal(),
  }));

  public readonly activeOrgIdAndRole = computed(() => {
    const grant = this.activeOrganizationGrant();
    return { orgId: grant?.organizationId, role: grant?.roleDisplayName };
  });

  public constructor() {
    this.hydrateFromStorage();
  }

  /**
   * Updates the current user information in the store.
   * Note: Side effects like starting inactivity tracking should be handled by AuthFacade.
   *
   * @param {User} user - The authenticated user data.
   */
  public setUser(user: User): void {
    this.userSignal.set(user);
    this.storageService.set(StorageServiceKeysMap.User, user);
    this.clearLogoutReason();
  }

  /**
   * Clears the stored logout reason.
   */
  public clearLogoutReason(): void {
    this.logoutReasonSignal.set(undefined);
    if (this.storageService.has(StorageServiceKeysMap.LogoutReason)) {
      this.storageService.remove(StorageServiceKeysMap.LogoutReason);
    }
  }

  /**
   * Updates the authentication tokens by merging existing tokens with new ones
   * and persists specific tokens when provided.
   *
   * @param {Partial<AuthStateTokens>} tokens - Partial object containing one or more authentication tokens to update.
   * @returns {void} No return value.
   */
  public updateTokens(tokens: Partial<AuthStateTokens>): void {
    const nextTokens: AuthStateTokens = {
      ...this.tokensSignal(),
      ...tokens,
    };
    this.tokensSignal.set(nextTokens);
    if ('accessTokenKerberos' in tokens) {
      this.persistToken(StorageServiceKeysMap.AccessTokenKerberos, tokens.accessTokenKerberos);
    }
    if ('refreshTokenKerberos' in tokens) {
      this.persistToken(StorageServiceKeysMap.RefreshTokenKerberos, tokens.refreshTokenKerberos);
    }
    if ('accessTokenHanko' in tokens) {
      this.persistToken(StorageServiceKeysMap.AccessTokenHanko, tokens.accessTokenHanko);
    }
  }

  /**
   * Records which identity provider established the current session and persists
   * it so the provider survives a page reload. Set by the login orchestration
   * when the Hanko session is exchanged for a platform token.
   *
   * @param {AuthSource} source - The provider that established the session
   */
  public setAuthSourceMarker(source: AuthSource): void {
    this.authSourceMarkerSignal.set(source);
    this.storageService.set(StorageServiceKeysMap.AuthSource, source);
  }

  /**
   * Clears all stored authentication tokens by setting their values to null.
   * This includes the Kerberos access and refresh tokens and the Hanko session token.
   *
   * @returns {void} This method does not return a value.
   */
  public clearTokens(): void {
    this.updateTokens({
      // eslint-disable-next-line unicorn/no-null
      accessTokenKerberos: null,
      // eslint-disable-next-line unicorn/no-null
      refreshTokenKerberos: null,
      // eslint-disable-next-line unicorn/no-null
      accessTokenHanko: null,
    });
  }

  /**
   * Sets the logout reason in the store.
   * Note: Full logout orchestration with side effects should be handled by AuthFacade.
   *
   * @param {LogoutReason} logoutReason - The reason for logging out.
   */
  public setLogoutReason(logoutReason: LogoutReason): void {
    this.logoutReasonSignal.set(logoutReason);
    this.storageService.set(StorageServiceKeysMap.LogoutReason, logoutReason);
  }

  /**
   * Sets the own grants in the store.
   *
   * @param {ResponseUserGrantDto[] | undefined} grants - User grants to set.
   */
  public setOwnGrants(grants: readonly ResponseUserGrantDto[] | undefined): void {
    this.ownGrantsSignal.set(grants);
  }

  /**
   * Sets the organizations in the store.
   *
   * @param {ResponseLeanOrganizationDto[] | undefined} organizations - Organizations to set.
   */
  public setOrganizations(organizations: readonly ResponseLeanOrganizationDto[] | undefined): void {
    this.organizationsSignal.set(organizations);
  }

  /**
   * Sets the error message in the store.
   *
   * @param {string | undefined} message - Error message to set.
   */
  public setErrorMessage(message: string | undefined): void {
    this.errorMessageSignal.set(message);
  }

  /**
   * Sets the organization ID in the store.
   * Note: Side effects like loading work item types should be handled by AuthFacade.
   *
   * @param {string} organizationId - The identifier of the organization.
   */
  public setOrganizationId(organizationId: string): void {
    this.organizationIdSignal.set(organizationId);
    this.storageService.set(StorageServiceKeysMap.OrganizationId, organizationId);
  }

  /**
   * Sets the organization industry in the store.
   *
   * @param {Industry | undefined} industry - The industry of the organization.
   */
  public setOrganizationIndustry(industry: Industry | undefined): void {
    this.organizationIndustrySignal.set(industry);
  }

  /**
   * Select and return authentication tokens along with the user's own grant.
   *
   * @param {FeaturesAmlDeskWithGrants} [feature] - Optional feature that may influence grant resolution.
   * @param {NamedRole[]} [requiredRoles] - Optional array of roles required to resolve the user's grant.
   * @returns {Signal<AuthStateTokenAndOwnGrant>} A signal that emits an object containing authentication state tokens and the user's own grant.
   */
  public selectAuthTokensAndOwnGrant(
    feature?: FeaturesAmlDeskWithGrants,
    requiredRoles?: NamedRole[],
  ): Signal<AuthStateTokenAndOwnGrant> {
    return computed(() => ({
      authStateTokens: this.tokensSignal(),
      ownGrant: this.resolveGrant(requiredRoles ?? (feature ? FeatureRoleMap[feature] : undefined)),
    }));
  }

  /**
   * Saves the provided grants and organizations data to the storage.
   *
   * @param {ResponseUserGrantDto[]} grants - An array of user grant data to be saved.
   * @param {ResponseLeanOrganizationDto[]} organizations - An array of organization data to be saved.
   * @returns {void} This method does not return a value.
   */
  public saveGrantsToStorage(grants: ResponseUserGrantDto[], organizations: ResponseLeanOrganizationDto[]): void {
    this.storageService.set(StorageServiceKeysMap.OwnGrants, grants);
    this.storageService.set(StorageServiceKeysMap.Organizations, organizations);
  }

  /**
   * Marks the grant loading process as complete or not complete and updates the storage service accordingly.
   *
   * @param {boolean} value - A boolean flag indicating whether the grant loading has been completed (true) or not (false).
   * @returns {void} This method does not return any value.
   */
  public markGrantLoadingComplete(value: boolean): void {
    this.grantLoadingCompleteSignal.set(value);
    if (value) {
      this.storageService.set(StorageServiceKeysMap.GrantLoadingComplete, true);
    } else if (this.storageService.has(StorageServiceKeysMap.GrantLoadingComplete)) {
      this.storageService.remove(StorageServiceKeysMap.GrantLoadingComplete);
    }
  }

  /**
   * Sets the grant loading state.
   *
   * @param {GrantLoadingState} state - The grant loading state to set.
   */
  public setGrantLoadingState(state: GrantLoadingState): void {
    this.grantLoadingStateSignal.set(state);
  }

  /**
   * Resets the state by clearing all signals and resetting tokens.
   * This method ensures that all related states are cleaned to a default state.
   *
   * @returns {void} No return value.
   */
  public resetState(): void {
    this.userSignal.set(undefined);
    this.ownGrantsSignal.set(undefined);
    this.organizationsSignal.set(undefined);
    this.organizationIdSignal.set(undefined);
    this.organizationIndustrySignal.set(undefined);
    this.errorMessageSignal.set(undefined);
    this.grantLoadingCompleteSignal.set(false);
    this.grantLoadingStateSignal.set(GrantLoadingStateMap.Idle);
    this.authSourceMarkerSignal.set(undefined);
    this.clearTokens();
  }

  /**
   * Determines the organization ID based on provided grants, organizations, and stored data.
   *
   * @param {ResponseUserGrantDto[]} grants - List of user grants, each containing an organization ID.
   * @param {ResponseLeanOrganizationDto[]} organizations - List of organizations, each containing an ID.
   * @returns {string | undefined} The resolved organization ID, or undefined if no suitable ID is found.
   */
  public determineOrganizationId(
    grants: readonly ResponseUserGrantDto[],
    organizations: readonly ResponseLeanOrganizationDto[],
  ): string | undefined {
    const organizationIdsFromGrants = grants.reduce<UUID[]>((accumulator, grant) => {
      if (grant.organizationId) {
        accumulator.push(grant.organizationId);
      }
      return accumulator;
    }, []);

    const organizationIds: UUID[] =
      organizationIdsFromGrants.length > 0
        ? organizationIdsFromGrants
        : organizations.reduce<UUID[]>((accumulator, organization) => {
            if (organization.id) {
              accumulator.push(organization.id);
            }
            return accumulator;
          }, []);

    const storedOrgId = this.storageService.get<UUID | null>(StorageServiceKeysMap.OrganizationId);
    if (storedOrgId && organizationIds.includes(storedOrgId)) {
      return storedOrgId;
    }

    const prioritizedOrgId =
      this.findOrganizationIdByRoles(grants, [NamedRoleMap.RiskAnalysisCustomer], organizationIds) ??
      this.findOrganizationIdByRoles(
        grants,
        [NamedRoleMap.WorkItemUser, NamedRoleMap.KycRiskEvaluationApiUser, NamedRoleMap.LocationAnalysisUser],
        organizationIds,
      );

    const resolvedOrgId = prioritizedOrgId ?? organizationIds[0];
    if (resolvedOrgId) {
      this.storageService.set(StorageServiceKeysMap.OrganizationId, resolvedOrgId);
      return resolvedOrgId;
    }
    return storedOrgId ?? undefined;
  }

  /**
   * Finds the organization ID that matches one of the prioritized roles, ensuring it is within the list of valid organization IDs.
   *
   * @param {ResponseUserGrantDto[]} grants - An array of user grant objects containing roles and organization information.
   * @param {NamedRole[]} prioritizedRoles - An array of roles sorted by priority to be matched against the grants.
   * @param {readonly UUID[]} validOrganizationIds - A list of organization IDs considered valid for matching.
   * @returns {UUID | undefined} The organization ID that matches the criteria, or undefined if no match is found.
   */
  private findOrganizationIdByRoles(
    grants: readonly ResponseUserGrantDto[],
    prioritizedRoles: readonly NamedRole[],
    validOrganizationIds: readonly UUID[],
  ): UUID | undefined {
    for (const role of prioritizedRoles) {
      const matchingGrant = grants.find(
        (grant) =>
          grant.roleDisplayName === role &&
          Boolean(grant.organizationId) &&
          validOrganizationIds.includes(grant.organizationId!),
      );
      if (matchingGrant?.organizationId) {
        return matchingGrant.organizationId;
      }
    }
    return undefined;
  }

  /**
   * Resolves a user grant based on the required roles and the user's own grants.
   *
   * @param {NamedRole[]} [requiredRoles] The roles that are required to be matched. If not provided, all roles defined in NamedRoleMap will be considered.
   * @returns {ResponseUserGrantDto | undefined} The resolved user grant that matches the required roles, or undefined if no match is found.
   */
  private resolveGrant(requiredRoles?: NamedRole[]): ResponseUserGrantDto | undefined {
    const ownGrants = this.ownGrantsSignal();
    if (!ownGrants || ownGrants.length === 0) {
      return undefined;
    }
    const organizationId = this.organizationIdSignal();
    const relevantRoles = requiredRoles ?? Object.values(NamedRoleMap);
    const relevantRolesWithoutBasicUser = new Set<NamedRole>(
      relevantRoles.filter((role) => role !== NamedRoleMap.BasicUser),
    );

    const matchWithinOrganization = organizationId
      ? ownGrants.find(
          (grant) =>
            grant.organizationId === organizationId && relevantRolesWithoutBasicUser.has(grant.roleDisplayName),
        )
      : undefined;

    if (matchWithinOrganization) {
      return matchWithinOrganization;
    }

    // Platform-level grants (no organizationId) are valid in any organization context.
    const platformLevelMatch = ownGrants.find(
      (grant) => !grant.organizationId && relevantRolesWithoutBasicUser.has(grant.roleDisplayName),
    );
    if (platformLevelMatch) {
      return platformLevelMatch;
    }

    if (relevantRoles.includes(NamedRoleMap.BasicUser)) {
      const basicUserMatch = ownGrants.find(
        (grant) =>
          (grant.organizationId === organizationId || !grant.organizationId) &&
          grant.roleDisplayName === NamedRoleMap.BasicUser,
      );
      if (basicUserMatch) {
        return basicUserMatch;
      }
    }

    return organizationId && requiredRoles
      ? undefined
      : (ownGrants.find((grant) => relevantRolesWithoutBasicUser.has(grant.roleDisplayName)) ??
          ownGrants.find((grant) => relevantRoles.includes(grant.roleDisplayName)));
  }

  /**
   * Decodes the Kerberos user id (`sub`) from a Kerberos access token
   *
   * @param {string | null} accessTokenKerberos - The Kerberos access token.
   * @returns {UUID | undefined} The Kerberos user id, or `undefined` when unavailable.
   */
  private getKerberosUserId(accessTokenKerberos: string | null): UUID | undefined {
    if (!accessTokenKerberos) {
      return;
    }
    try {
      return jwtDecode<AdpJwtPayload>(accessTokenKerberos).sub;
    } catch {
      return undefined;
    }
  }

  /**
   * Persists a token in the storage or removes it if the value is null or undefined.
   *
   * @param {string} key - The key under which the token should be stored or removed.
   * @param {string | null | undefined} value - The token value to store. If null or undefined, the token will be removed.
   * @returns {void} This method does not return a value.
   */
  private persistToken(key: string, value: string | null | undefined): void {
    if (value) {
      this.storageService.set(key, value);
    } else if (this.storageService.has(key)) {
      this.storageService.remove(key);
    }
  }

  /**
   * Restores application state from storage and updates corresponding signals with the retrieved data.
   * This method fetches various user-related and application-state data from the storage and sets it
   * to appropriate reactive signals for managing state within the application.
   *
   * @returns {void} Does not return any value.
   * All relevant data is hydrated and set to the appropriate signals from stored persistent data.
   */
  private hydrateFromStorage(): void {
    hydrateMultiple(this.storageService, {
      [StorageServiceKeysMap.User]: this.userSignal,
      [StorageServiceKeysMap.LogoutReason]: this.logoutReasonSignal,
      [StorageServiceKeysMap.OwnGrants]: this.ownGrantsSignal,
      [StorageServiceKeysMap.Organizations]: this.organizationsSignal,
      [StorageServiceKeysMap.OrganizationId]: this.organizationIdSignal,
    });
    this.sanitizeArraySignal(this.ownGrantsSignal, StorageServiceKeysMap.OwnGrants);
    this.sanitizeArraySignal(this.organizationsSignal, StorageServiceKeysMap.Organizations);

    const grantLoadingComplete = this.storageService.get<boolean>(StorageServiceKeysMap.GrantLoadingComplete);
    if (grantLoadingComplete) {
      this.grantLoadingCompleteSignal.set(true);
    }

    /* eslint-disable unicorn/no-null -- AuthTokens models a missing token as null */
    this.tokensSignal.set({
      accessTokenKerberos: this.storageService.get<string>(StorageServiceKeysMap.AccessTokenKerberos) ?? null,
      refreshTokenKerberos: this.storageService.get<string>(StorageServiceKeysMap.RefreshTokenKerberos) ?? null,
      accessTokenHanko: this.storageService.get<string>(StorageServiceKeysMap.AccessTokenHanko) ?? null,
    });
    /* eslint-enable unicorn/no-null */

    this.authSourceMarkerSignal.set(this.storageService.get<AuthSource>(StorageServiceKeysMap.AuthSource) ?? undefined);
  }

  /**
   * Sanitizes a signal that holds either an array or undefined. Ensures the signal does not contain
   * invalid values and removes associated storage if necessary.
   *
   * @template TValue The type of elements in the array.
   * @param {WritableSignal<TValue[] | undefined>} targetSignal The writable signal to be sanitized. It should hold a value of type readonly TValue[] or undefined.
   * @param {StorageServiceKeys} storageKey The key used in the storage service to identify associated stored data that might need to be removed.
   * @returns {void} This method does not return any value.
   */
  private sanitizeArraySignal<TValue>(
    targetSignal: WritableSignal<readonly TValue[] | undefined>,
    storageKey: StorageServiceKeys,
  ): void {
    const value = targetSignal();
    if (value === undefined || Array.isArray(value)) {
      return;
    }
    targetSignal.set(undefined);
    if (this.storageService.has(storageKey)) {
      this.storageService.remove(storageKey);
    }
  }
}
